import { Component, Suspense, lazy, type ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SWRConfig } from "swr";
import { useAuth } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { ConfirmDialogProvider } from "@/contexts/ConfirmDialogContext";
import { authFetcher } from "@/lib/fetcher";
import { Loader2 } from "lucide-react";
import posthog from "posthog-js";
import "./index.css";

const Dashboard = lazy(() => import("@/pages/Dashboard").then((m) => ({ default: m.Dashboard })));
const Inbox = lazy(() => import("@/pages/Inbox").then((m) => ({ default: m.Inbox })));
const AdAnalytics = lazy(() => import("@/pages/AdAnalytics").then((m) => ({ default: m.AdAnalytics })));
const AdQualityAnalytics = lazy(() => import("@/pages/AdQualityAnalytics"));
const LeadsCenter = lazy(() => import("@/pages/LeadsCenter"));
const Conversions = lazy(() => import("@/pages/Conversions").then((m) => ({ default: m.Conversions })));
const CloseCRM = lazy(() => import("@/pages/CloseCRM").then((m) => ({ default: m.CloseCRM })));
const OnceHub = lazy(() => import("@/pages/OnceHub").then((m) => ({ default: m.OnceHub })));
const Calendly = lazy(() => import("@/pages/Calendly").then((m) => ({ default: m.Calendly })));
const SalesKick = lazy(() => import("@/pages/SalesKick").then((m) => ({ default: m.SalesKick })));
const RevioCrm = lazy(() => import("@/pages/RevioCrm").then((m) => ({ default: m.RevioCrm })));
const ESignatures = lazy(() => import("@/pages/ESignatures").then((m) => ({ default: m.ESignatures })));
const PandaDoc = lazy(() => import("@/pages/PandaDoc").then((m) => ({ default: m.PandaDoc })));
const Docusign = lazy(() => import("@/pages/Docusign").then((m) => ({ default: m.Docusign })));
const Stripe = lazy(() => import("@/pages/Stripe").then((m) => ({ default: m.Stripe })));
const Whop = lazy(() => import("@/pages/Whop").then((m) => ({ default: m.Whop })));
const Payva = lazy(() => import("@/pages/Payva").then((m) => ({ default: m.Payva })));
const Elective = lazy(() => import("@/pages/Elective").then((m) => ({ default: m.Elective })));
const FanBasis = lazy(() => import("@/pages/FanBasis").then((m) => ({ default: m.FanBasis })));
const Revenue = lazy(() => import("@/pages/Revenue").then((m) => ({ default: m.Revenue })));
const ConversationsPage = lazy(() => import("@/pages/Conversations").then((m) => ({ default: m.Conversations })));
const SetterAnalysis = lazy(() => import("@/pages/SetterAnalysis").then((m) => ({ default: m.SetterAnalysis })));
const Integrations = lazy(() => import("@/pages/Integrations").then((m) => ({ default: m.Integrations })));
const Settings = lazy(() => import("@/pages/Settings").then((m) => ({ default: m.Settings })));
const CampaignDetails = lazy(() => import("@/pages/CampaignDetails").then((m) => ({ default: m.CampaignDetails })));
const DeveloperDashboard = lazy(() =>
  import("@/pages/DeveloperDashboard").then((m) => ({ default: m.DeveloperDashboard })),
);
const SignIn = lazy(() => import("@/pages/SignIn").then((m) => ({ default: m.SignIn })));
const AuthCallback = lazy(() => import("@/pages/AuthCallback").then((m) => ({ default: m.AuthCallback })));
const FacebookInstagramCallback = lazy(() =>
  import("@/pages/FacebookInstagramCallback").then((m) => ({ default: m.FacebookInstagramCallback })),
);
const GoogleCalendarCallback = lazy(() =>
  import("@/pages/GoogleCalendarCallback").then((m) => ({ default: m.GoogleCalendarCallback })),
);
const HubSpotCallback = lazy(() => import("@/pages/HubSpotCallback").then((m) => ({ default: m.HubSpotCallback })));
const CloseCallback = lazy(() => import("@/pages/CloseCallback").then((m) => ({ default: m.CloseCallback })));

function getErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
}

function isExpectedClientError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === 401) return true;

  const expected = !!(error && typeof error === "object" && (error as { expected?: boolean }).expected);
  if (expected) return true;

  const message = error instanceof Error ? error.message : String(error || "");
  return /not authenticated|invalid refresh token|aborterror|request was aborted/i.test(message);
}

function FullScreenSpinner() {
  return (
    <div className="grid place-items-center min-h-screen bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// Error boundary for runtime errors
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error) {
    posthog.captureException(error, { context: "runtime_error" });
  }

  override componentDidUpdate(prevProps: { children: ReactNode }) {
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="grid place-items-center min-h-[50vh] p-6">
          <div className="text-center space-y-4">
            <h2 className="text-lg font-medium">Something went wrong</h2>
            <p className="text-muted-foreground text-sm">An unexpected error occurred. Please reload the page.</p>
            <button
              onClick={this.handleReload}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function App() {
  const { loading } = useAuth();

  if (loading) {
    return <FullScreenSpinner />;
  }

  return (
    <ThemeProvider>
      <ConfirmDialogProvider>
        <SidebarProvider>
          <SWRConfig
            value={{
              // Global fetcher
              fetcher: authFetcher,

              // Performance optimizations
              revalidateOnFocus: true,
              revalidateOnReconnect: true,
              dedupingInterval: 2000,
              focusThrottleInterval: 5000,

              // Eliminate UI flicker by keeping stale data during revalidation
              keepPreviousData: true,

              // Smart error retry with exponential backoff
              errorRetryCount: 3,
              errorRetryInterval: 5000,
              shouldRetryOnError: true,
              onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
                if (isExpectedClientError(error)) {
                  return;
                }

                // Don't retry on 4xx client errors (except 429 rate limit)
                const status = getErrorStatus(error);
                if (status && status >= 400 && status < 500) {
                  if (status === 429) {
                    // Respect Retry-After header for rate limits
                    const retryAfter =
                      error && typeof error === "object" && "headers" in error
                        ? (error as { headers?: Headers }).headers?.get("Retry-After")
                        : null;
                    const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 60000;
                    console.log(`[SWR] Rate limited on ${key}, retrying in ${waitTime}ms`);
                    setTimeout(() => revalidate({ retryCount }), waitTime);
                    return;
                  }
                  // Don't retry 401 (auth), 403 (forbidden), 404 (not found), etc.
                  console.error(`[SWR] Not retrying ${status} error for ${key}`);
                  return;
                }

                // Don't retry after max attempts
                if (retryCount >= 3) {
                  console.error(`[SWR] Max retries (${retryCount}) reached for ${key}`);
                  return;
                }

                // Exponential backoff: 5s, 10s, 20s
                const backoffTime = 5000 * Math.pow(2, retryCount);
                console.log(`[SWR] Retrying ${key} in ${backoffTime}ms (attempt ${retryCount + 1}/3)`);
                setTimeout(() => revalidate({ retryCount }), backoffTime);
              },

              // Global error handler - sends to PostHog for monitoring
              onError: (error, key) => {
                if (isExpectedClientError(error)) {
                  return;
                }
                console.error(`[SWR Error] ${key}:`, error);
                // Use captureException for proper error tracking with stack traces
                posthog.captureException(error instanceof Error ? error : new Error(String(error)), {
                  swr_key: key,
                });
              },
            }}
          >
            <BrowserRouter>
              <RealtimeProvider>
                <ErrorBoundary>
                  <Suspense fallback={<FullScreenSpinner />}>
                    <Routes>
                      <Route path="/signin" element={<SignIn />} />
                      <Route path="/sign-in" element={<Navigate to="/signin" replace />} />
                      <Route path="/signup" element={<Navigate to="/signin" replace />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/auth/confirm" element={<AuthCallback />} />
                      <Route path="/auth/facebook-instagram/callback" element={<FacebookInstagramCallback />} />
                      <Route path="/auth/google/callback" element={<GoogleCalendarCallback />} />
                      <Route path="/auth/hubspot/callback" element={<HubSpotCallback />} />
                      <Route path="/close-callback" element={<CloseCallback />} />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/inbox"
                        element={
                          <ProtectedRoute>
                            <Inbox />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/debug-ad-analytics"
                        element={
                          <ProtectedRoute>
                            <AdAnalytics />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ad-quality-analytics"
                        element={
                          <ProtectedRoute>
                            <AdQualityAnalytics />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/leads-center"
                        element={
                          <ProtectedRoute>
                            <LeadsCenter />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/debug-conversions"
                        element={
                          <ProtectedRoute>
                            <Conversions />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/close-crm"
                        element={
                          <ProtectedRoute>
                            <CloseCRM />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/oncehub"
                        element={
                          <ProtectedRoute>
                            <OnceHub />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/calendly"
                        element={
                          <ProtectedRoute>
                            <Calendly />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/saleskick"
                        element={
                          <ProtectedRoute>
                            <SalesKick />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/revio-crm"
                        element={
                          <ProtectedRoute>
                            <RevioCrm />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/esignatures"
                        element={
                          <ProtectedRoute>
                            <ESignatures />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/pandadoc"
                        element={
                          <ProtectedRoute>
                            <PandaDoc />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/docusign"
                        element={
                          <ProtectedRoute>
                            <Docusign />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/stripe"
                        element={
                          <ProtectedRoute>
                            <Stripe />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/whop"
                        element={
                          <ProtectedRoute>
                            <Whop />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/payva"
                        element={
                          <ProtectedRoute>
                            <Payva />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/elective"
                        element={
                          <ProtectedRoute>
                            <Elective />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/fanbasis"
                        element={
                          <ProtectedRoute>
                            <FanBasis />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/revenue"
                        element={
                          <ProtectedRoute>
                            <Revenue />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/conversations"
                        element={
                          <ProtectedRoute>
                            <ConversationsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/setter-analysis"
                        element={
                          <ProtectedRoute>
                            <SetterAnalysis />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/integrations"
                        element={
                          <ProtectedRoute>
                            <Integrations />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute>
                            <Settings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/debug-dev"
                        element={
                          <ProtectedRoute>
                            <DeveloperDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/developer-dashboard" element={<Navigate to="/debug-dev" replace />} />
                      <Route path="/dev-debug" element={<Navigate to="/debug-dev" replace />} />
                      <Route
                        path="/campaign/:id"
                        element={
                          <ProtectedRoute>
                            <CampaignDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/campaign/:id/adset/:adsetId"
                        element={
                          <ProtectedRoute>
                            <CampaignDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/campaign/:id/adset/:adsetId/ad/:adId"
                        element={
                          <ProtectedRoute>
                            <CampaignDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/campaign/:id/ad/:adId"
                        element={
                          <ProtectedRoute>
                            <CampaignDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </RealtimeProvider>
            </BrowserRouter>
          </SWRConfig>
        </SidebarProvider>
      </ConfirmDialogProvider>
    </ThemeProvider>
  );
}

export default App;
